// Shadow of VLT 8.0.0-alpha.28 Navigation.tsx — enables the fat menu on guides
// (DR-2026-302 v0.2.x). Third instance of the fleet pattern: ekonomi
// (DR-2026-248, grouped variant) → wantai (raw children) → guides (this file).
//
// DELTA vs wantai's copy — and the trap it records (DR-2026-302 v0.2.1): the
// first cut of this file was byte-identical to wantai's, which faithfully
// transplanted wantai's STATIC selling-deck nav item (a nonContentRoute,
// /en/deck) onto a tenant where that route does not exist — a dead "Deck"
// item rendered live until caught. Byte-identity with a sibling tenant's
// shadow is NOT the success criterion; mechanism-identity is. When copying a
// fleet shadow, read it WHOLE and strip tenant-specific features first.
//
// WHY A SHADOW (not a data setting): VLT gates the fat menu on
// `has_fat_menu` read from the `voltolighttheme.header` inherit behavior, but
// nick does not provide that behavior (verified on the wantai nick, recorded
// in volto-wantai's copy of this shadow), so the data-flag path is a dead
// end. The shadow removes the `hasFatMenu` gating so any top item WITH
// children renders the fat-menu panel; a childless item renders a plain
// NavItem. Markup + class names are byte-identical to upstream so VLT's CSS
// and a11y behaviour apply unchanged.
//
// On guides the top items are the DR-2026-302 sections (Marketing /
// Compliance / Platform notes), so the panel shows each section's guides —
// the nav bar itself never grows with content.
//
// Re-verify against upstream on any VLT bump, together with the ekonomi and
// wantai copies — one bump, three shadows, one review.
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { NavLink } from 'react-router-dom';
import doesNodeContainClick from '@kitconcept/volto-light-theme/helpers/doesNodeContainClick';
import { useIntl, defineMessages } from 'react-intl';
import cx from 'classnames';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import config from '@plone/volto/registry';
import { getNavigation } from '@plone/volto/actions/navigation/navigation';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import clearSVG from '@plone/volto/icons/clear.svg';
import NavItem from '@plone/volto/components/theme/Navigation/NavItem';

const messages = defineMessages({
  closeMenu: { id: 'Close menu', defaultMessage: 'Close menu' },
  openFatMenu: { id: 'Open menu', defaultMessage: 'Open menu' },
});

const Navigation = ({ pathname }) => {
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(null);
  const [currentOpenIndex, setCurrentOpenIndex] = useState(null);
  const navigation = useRef(null);
  const dispatch = useDispatch();
  const intl = useIntl();
  const lang = useSelector((state) => state.intl.locale);
  const token = useSelector((state) => state.userSession.token, shallowEqual);
  const items = useSelector((state) => state.navigation.items, shallowEqual);

  const closeMenu = () => {
    setDesktopMenuOpen(null);
    setCurrentOpenIndex(null);
  };

  // this function doesn't close the navigation when clicking the scrollbar
  const doesScrollbarContainClick = (event) => {
    const clickedVerticalScrollbar =
      event.clientX >= document.documentElement.clientWidth &&
      event.clientX <= window.innerWidth;
    const clickedHorizontalScrollbar =
      event.clientY >= document.documentElement.clientHeight &&
      event.clientY <= window.innerHeight;
    return clickedVerticalScrollbar || clickedHorizontalScrollbar;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navigation.current && doesNodeContainClick(navigation.current, event))
        return;
      if (doesScrollbarContainClick(event)) return;
      closeMenu();
    };
    document.addEventListener('mousedown', handleClickOutside, false);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  }, []);

  useEffect(() => {
    if (!hasApiExpander('navigation', getBaseUrl(pathname))) {
      dispatch(getNavigation(getBaseUrl(pathname), config.settings.navDepth));
    }
  }, [pathname, token, dispatch]);

  const isActive = (url) =>
    (url === '' && pathname === '/') || (url !== '' && pathname === url);

  const openMenu = (index) => {
    if (index === currentOpenIndex) {
      setDesktopMenuOpen(null);
      setCurrentOpenIndex(null);
    } else {
      setDesktopMenuOpen(index);
      setCurrentOpenIndex(index);
    }
  };

  // DR-2026-284 D-2: hovering a top-level item other than the open one dismisses
  // the fat menu — so moving onto a menuless item (on guides: "Compliance"
  // while its only child is private) closes the
  // panel and clears the open item's active-underline, instead of leaving two
  // underlines and an open panel while the pointer is elsewhere. Click still
  // opens; hover never auto-opens. Entering the open item's own panel does NOT
  // close it: the panel is a child of that item's <li>, so no mouseEnter fires on
  // a different item and the `open !== index` guard holds.
  const dismissIfHoveringOther = (index) => {
    if (currentOpenIndex !== null && currentOpenIndex !== index) closeMenu();
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' || event.keyCode === 27) closeMenu();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  return (
    <nav
      id="navigation"
      aria-label="navigation"
      className="navigation"
      ref={navigation}
    >
      <div className={'computer large screen widescreen only'}>
        <ul className="desktop-menu">
          {items.map((item, index) => (
            <li
              key={item.url}
              onMouseEnter={() => dismissIfHoveringOther(index)}
            >
              {item.items && item.items.length > 0 ? (
                <>
                  <button
                    onClick={() => openMenu(index)}
                    className={cx('item', {
                      active:
                        desktopMenuOpen === index ||
                        (!desktopMenuOpen && pathname === item.url),
                    })}
                    aria-label={intl.formatMessage(messages.openFatMenu)}
                    aria-expanded={desktopMenuOpen === index}
                  >
                    {item.title}
                  </button>
                  <div className="submenu-wrapper">
                    <div
                      className={cx('submenu', {
                        active: desktopMenuOpen === index,
                      })}
                    >
                      <div className="submenu-inner">
                        <NavLink
                          to={item.url === '' ? '/' : item.url}
                          onClick={() => closeMenu()}
                          className="submenu-header"
                        >
                          <h2>{item.nav_title ?? item.title}</h2>
                        </NavLink>
                        <button
                          className="close"
                          onClick={closeMenu}
                          aria-label={intl.formatMessage(messages.closeMenu)}
                        >
                          <Icon name={clearSVG} size="48px" />
                        </button>
                        <ul>
                          {item.items.map((subitem) => (
                            <li className="subitem-wrapper" key={subitem.url}>
                              <NavLink
                                to={subitem.url}
                                onClick={() => closeMenu()}
                                className={cx({
                                  current: isActive(subitem.url),
                                })}
                              >
                                <span className="left-arrow">&#8212;</span>
                                <span>{subitem.nav_title || subitem.title}</span>
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <NavItem item={item} lang={lang} key={item.url} />
              )}
            </li>
          ))}
          {/* NOTE (guides): wantai's copy appends a static nav item here — its
              selling-deck nonContentRoute (/en/deck, DR-2026-261/264). That is
              a WANTAI feature, not part of the fat-menu mechanism; it was
              transplanted by the byte-identical copy and rendered a dead
              "Deck → /en/deck" item on guides until removed (BUG below in
              DR-2026-302 v0.2.1). Guides has no static nav items: the nav is
              the content tree, period. */}
        </ul>
      </div>
    </nav>
  );
};

export default Navigation;
